import {
	type Attempt,
	isCorrect,
	material,
	type ReviewQuestion,
} from "../lib/review";

type Props = {
	correctCount: number;
	reviewCount: number;
	attempts: Record<string, Attempt>;
	questions: ReviewQuestion[];
	onReviewAll: () => void;
	onReviewMissed: () => void;
};

export function Completion({
	correctCount,
	reviewCount,
	attempts,
	questions,
	onReviewAll,
	onReviewMissed,
}: Props) {
	const missedQuestions = questions.filter((item) => {
		const attempt = attempts[item.id];
		return attempt && !isCorrect(attempt.status);
	});
	const missedConcepts = [
		...new Set(missedQuestions.map((item) => item.question.keyConcept)),
	];
	const missedChapters = [
		...new Set(missedQuestions.map((item) => item.chapterIndex)),
	].map((chapterIndex) => material[chapterIndex]?.chapterTitle);
	return (
		<main className="completion-shell">
			<section className="completion-card">
				<span className="completion-mark" aria-hidden="true">
					✦
				</span>
				<p className="eyebrow">REVIEW COMPLETE</p>
				<h1>You’ve completed this review round.</h1>
				<p className="completion-copy">
					You recalled {correctCount} of {questions.length} concepts correctly.
					A review round shows what to revisit—it doesn’t define your readiness.
				</p>
				<div
					className={`completion-stats ${reviewCount === 0 ? "without-review" : ""}`}
				>
					<div>
						<strong>✓ {correctCount}</strong>
						<span>Correct</span>
					</div>
					{reviewCount > 0 && (
						<div className="review-stat">
							<strong>↺ {reviewCount}</strong>
							<span>Needs review</span>
						</div>
					)}
				</div>
				{missedConcepts.length > 0 ? (
					<div className="missed-concepts">
						<p>
							<b>Concepts to revisit:</b> {missedConcepts.join(", ")}
						</p>
						<p>
							<b>Chapters to revisit:</b> {missedChapters.join(", ")}
						</p>
					</div>
				) : (
					<p className="missed-concepts">
						<b>No missed chapters this round.</b> Keep practicing to strengthen
						recall.
					</p>
				)}
				<div className="completion-actions">
					<button
						className="secondary-button"
						type="button"
						onClick={onReviewAll}
					>
						Review all chapters
					</button>
					<button
						className="next-button"
						type="button"
						onClick={onReviewMissed}
						disabled={reviewCount === 0}
					>
						Review missed chapters <span aria-hidden="true">→</span>
					</button>
				</div>
			</section>
		</main>
	);
}
